import { useSnackbarStore, type Snackbar, type SnackbarType } from '@/stores/snackbarStore';
import { AchievementBadgeIcon } from '@/components/AchievementBadgeIcon';

const shellStyles: Record<SnackbarType, string> = {
  success:
    'border-white/10 bg-[linear-gradient(180deg,rgba(18,24,38,0.96),rgba(12,18,30,0.96))] shadow-[0_14px_34px_rgba(0,0,0,0.26)]',
  error:
    'border-white/10 bg-[linear-gradient(180deg,rgba(18,24,38,0.96),rgba(12,18,30,0.96))] shadow-[0_14px_34px_rgba(0,0,0,0.26)]',
  warning:
    'border-white/10 bg-[linear-gradient(180deg,rgba(18,24,38,0.96),rgba(12,18,30,0.96))] shadow-[0_14px_34px_rgba(0,0,0,0.26)]',
  info: 'border-white/10 bg-[linear-gradient(180deg,rgba(18,24,38,0.96),rgba(12,18,30,0.96))] shadow-[0_14px_34px_rgba(0,0,0,0.26)]',
};

const accentStyles: Record<SnackbarType, string> = {
  success: 'bg-emerald-400/90',
  error: 'bg-rose-400/90',
  warning: 'bg-amber-300/90',
  info: 'bg-slate-300/90',
};

const iconStyles: Record<SnackbarType, string> = {
  success: 'text-emerald-300',
  error: 'text-rose-300',
  warning: 'text-amber-200',
  info: 'text-slate-200',
};

type TypeIconProps = {
  type: SnackbarType;
};

const TypeIcon = ({ type }: TypeIconProps) => {
  const iconClass = iconStyles[type];

  return (
    <span
      className={`inline-flex h-8 w-8 items-center justify-center rounded-xl border border-white/8 bg-white/4 ${iconClass}`}
      aria-hidden="true"
    >
      {type === 'success' ? (
        <svg className="h-4.5 w-4.5" viewBox="0 0 20 20" fill="none" stroke="currentColor">
          <path
            d="m5 10 3.1 3.2L15 6.8"
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : type === 'error' ? (
        <svg className="h-4.5 w-4.5" viewBox="0 0 20 20" fill="none" stroke="currentColor">
          <path d="M6 6l8 8M14 6l-8 8" strokeWidth="1.9" strokeLinecap="round" />
        </svg>
      ) : type === 'warning' ? (
        <svg className="h-4.5 w-4.5" viewBox="0 0 20 20" fill="none" stroke="currentColor">
          <path d="M10 4.5 16 15H4l6-10.5Z" strokeWidth="1.7" strokeLinejoin="round" />
          <path d="M10 8v3.6M10 13.7v.1" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      ) : (
        <svg className="h-4.5 w-4.5" viewBox="0 0 20 20" fill="none" stroke="currentColor">
          <circle cx="10" cy="10" r="6.2" strokeWidth="1.7" />
          <path d="M10 8.4v4M10 6.4v.1" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      )}
    </span>
  );
};

function SnackbarLeadingIcon({ snackbar }: { snackbar: Snackbar }) {
  if (snackbar.achievementId) {
    return (
      <span
        className="inline-flex h-9 w-9 items-center justify-center text-slate-100"
        aria-hidden="true"
      >
        <AchievementBadgeIcon achievementId={snackbar.achievementId} className="h-6 w-6" />
      </span>
    );
  }

  return <TypeIcon type={snackbar.type} />;
}

function SnackbarItem({ snackbar, onRemove }: { snackbar: Snackbar; onRemove: () => void }) {
  const duration = snackbar.duration ?? 5000;
  const isExiting = snackbar.isExiting ?? false;

  return (
    <div
      className={`overflow-hidden ${isExiting ? 'animate-snackbar-collapse' : 'mb-3'}`}
    >
      <div
        className={`
          ${shellStyles[snackbar.type]}
          relative overflow-hidden rounded-2xl border backdrop-blur-md
          ${isExiting ? 'animate-snackbar-out' : 'animate-snackbar-in'}
        `}
      >
        <div
          className={`absolute inset-y-4 left-0 w-[2px] rounded-r-full opacity-80 ${accentStyles[snackbar.type]}`}
          aria-hidden="true"
        />
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.03),transparent_40%)]" />
        <div className="relative flex items-start gap-3 px-4 py-3">
          <SnackbarLeadingIcon snackbar={snackbar} />
          <div className="min-w-0 flex-1 pr-2">
            <p className="text-[13px] font-medium leading-5 text-slate-100/92">{snackbar.message}</p>
          </div>
          <button
            onClick={onRemove}
            className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-slate-400/80 transition-colors hover:bg-white/6 hover:text-slate-100"
            aria-label="Dismiss notification"
          >
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor">
              <path d="M6 6l8 8M14 6l-8 8" strokeWidth="1.7" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        {duration > 0 && (
          <div className="mx-4 mb-3 h-[2px] overflow-hidden rounded-full bg-white/6">
            <div
              className={`h-full origin-left rounded-full ${accentStyles[snackbar.type]} animate-timer-shrink`}
              style={{
                animationDuration: `${duration}ms`,
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export function SnackbarContainer() {
  const { snackbars, removeSnackbar } = useSnackbarStore();

  if (snackbars.length === 0) return null;

  return (
    <div className="pointer-events-none fixed right-4 top-20 z-100 flex w-[min(92vw,26rem)] flex-col sm:right-5 sm:top-24">
      {snackbars.map((snackbar) => (
        <div key={snackbar.id} className="pointer-events-auto">
          <SnackbarItem snackbar={snackbar} onRemove={() => removeSnackbar(snackbar.id)} />
        </div>
      ))}
    </div>
  );
}
